// campaign-state-bridge.tsx — the ONE place the redesign sets the legacy
// `CampaignReducer` UI-mode flags that the reused leaf components branch on
// (§3/§4 of the state-flow handoff). Every adapter here follows the same
// contract: snapshot the prior global value on mount, set the mode the surface
// needs, and RESTORE the prior value on unmount. That makes the redesign's
// surfaces mode-isolated — opening one never leaves a flag set that flips
// another surface into the wrong mode (the audience checkbox-bleed bug), and
// the legacy dashboard's own state is left exactly as it was found.

import React from 'react';
import { useStore } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { namespace } from '@common-config/constants';

import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import {
  setActiveBulkView,
  setActiveTab,
  setCurrentTemplate,
  setCampaignIdForLogsView,
  resetCampaignAndTemplateThunk,
  type BulkView,
  type BulkDashboardTab,
} from '@common/reducers/CampaignReducer';
import { bulkAudienceSelectBoardThunk } from '@common-reducers/BoardsThunks';
import { boardsActions } from '@common-reducers/BoardsReducer';
import {
  DBBoardsThunks,
  DBTemplateThunks,
  DBCampaignThunks,
  templateSelectOneObjectByQuery,
} from '@common-reducers/DBServiceThunks';
import { loadTemplateData, resetScheduledMessageEditor } from '@common/reducers/ScheduledMessageEditorReducer';
import type { CommonRootState } from '@common/types/common-root-state-type';
import { CampaignManagementView } from '@common/views/campaign-management-view/campaign-management-view';

import { openNewAudienceModal } from '../bt-bulk-dashboard/audiences/bulk-audiences-list';
import BulkLogsView from '../bt-bulk-dashboard/bulk-logs-view/bulk-logs-view';

// The audience picker adapter lives in its own module now — see
// `audience-list-adapter.tsx` for why (this file's `ManagementViewAdapter`
// drags in `CampaignManagementView`, which touches `window` at module scope and
// so cannot be in the graph of anything a node-env jest test reaches).
// Re-exported here so every existing import site is unchanged.
export { AudienceListAdapter, type AudienceListMode } from './audience-list-adapter';

// ─── ManagementViewAdapter ──────────────────────────────────────────────────
// The redesign's Audiences and Templates tabs render the FULL legacy management
// container (`CampaignManagementView`), not the leaf list. The container owns
// ALL the management sub-views: it shows `BulkAudiencesList` / `BulkTemplateDialog`
// for `activeBulkView === 'campaignManagementView'`, AND swaps to the audience
// BOARD editor (`BulkTaskViewContainer` — the board's contacts/tasks) or the
// template editor (`NewTemplateView`) when a row-click sets `activeBulkView` to
// `campaignBoardFromManagementView` / `campaignNewTemplateViewFromManagement`.
//
// Mounting only the leaf list (the old approach) was the checkbox-bleed bug:
// a row-click set `activeBulkView` to a board sub-view, but nothing rendered the
// board editor, so the leaf list stayed mounted and — seeing
// `activeBulkView !== 'campaignManagementView'` — flipped its rows into the
// wizard's checkbox SELECTION mode. Rendering the full container means the
// row-click now opens the board editor (the intended behaviour) instead.
//
// NOTE: `CampaignManagementView` previously routed the board sub-view to
// `BTBulkDashboard` (the campaign LAUNCH stepper) — so a row-click opened the
// "Audience → Message → Timing → Launch" flow, not the contacts editor (issue
// #97 / BUG 1). It now mirrors the canonical `CampaignsView` container and
// renders `BulkTaskViewContainer` / `NewTemplateView` for those sub-views, so no
// launch stepper can ever surface from clicking an audience or template here.
//
// `CampaignManagementView` drives `activeBulkView` itself (it resets to
// `campaignManagementView` on mount). This adapter additionally drives the
// management SUB-TAB (`manageAudiences` / `manageTemplates`) via `activeTab`,
// and — like `AudienceListAdapter` — snapshots the prior global flags on mount
// and restores them on unmount, so leaving the tab never leaves the legacy
// dashboard in a board-editor / wrong-tab state for any other surface.
export type ManagementSubTab = Extract<
  BulkDashboardTab,
  'manageAudiences' | 'manageTemplates'
>;

interface ManagementViewAdapterProps {
  subTab: ManagementSubTab;
}

export const ManagementViewAdapter: React.FC<ManagementViewAdapterProps> = ({ subTab }) => {
  const dispatch = useAppDispatch();
  const prevBulkView = useAppSelector((s: CommonRootState) => s.CampaignReducer.activeBulkView);
  const prevTab = useAppSelector((s: CommonRootState) => s.CampaignReducer.activeTab);
  // Snapshot prior values ONCE on mount (ref, so our own writes don't recapture).
  const restoreRef = React.useRef<{ bulkView: BulkView; tab: BulkDashboardTab }>({
    bulkView: prevBulkView,
    tab: prevTab,
  });

  React.useEffect(() => {
    const restore = restoreRef.current;
    // CampaignManagementView resets activeBulkView → 'campaignManagementView'
    // on its own mount; we set the management sub-tab the redesign tab selected.
    dispatch(setActiveTab(subTab));
    return () => {
      // Reset on unmount so a board-editor sub-view (opened by a row-click) or
      // the management sub-tab never leaks to another surface.
      dispatch(setActiveBulkView(restore.bulkView));
      dispatch(setActiveTab(restore.tab));
      dispatch(resetCampaignAndTemplateThunk());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, subTab]);

  // ── New-Audience modal: open state as a URL flag (?new=audience) ──
  // The New-Audience flow is a global import MODAL (overlay), not a routable
  // surface — so instead of a route we mirror its open state in the URL: the
  // Audiences-tab "New Audience" button navigates to ?new=audience (see
  // bulk-audiences-list), this opens the modal through the SAME Redux sequence
  // the button used (openNewAudienceModal — SSoT), and dismissing the modal
  // (which flips isImportModalOpen → false via the modal's own handleClose)
  // clears the flag. Net: reload/back restore the open modal; closing it is
  // reflected back into the URL. Only the Audiences tab participates.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantNewAudience = subTab === 'manageAudiences' && searchParams.get('new') === 'audience';
  const isImportModalOpen = useAppSelector((s: CommonRootState) => s.CampaignReducer.isImportModalOpen);
  const activeBulkView = useAppSelector((s: CommonRootState) => s.CampaignReducer.activeBulkView);
  const openedForFlagRef = React.useRef(false);
  React.useEffect(() => {
    if (!wantNewAudience) { openedForFlagRef.current = false; return; }
    if (isImportModalOpen) { openedForFlagRef.current = true; return; }
    // Flag set, modal currently closed:
    if (openedForFlagRef.current) {
      // …it was open and the user dismissed it → drop the flag (replace, so the
      // dismissed-modal URL isn't a back-button trap).
      openedForFlagRef.current = false;
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    } else {
      // …not opened yet (fresh navigation / cold reload with the flag) → open it.
      openNewAudienceModal(dispatch, activeBulkView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantNewAudience, isImportModalOpen]);

  // hideTabs: the redesign supplies its own Audiences/Templates/Campaigns tab
  // bar, so suppress the legacy in-container tab row to avoid a doubled control.
  return <CampaignManagementView hideTabs />;
};

// ─── CampaignSubEditor ──────────────────────────────────────────────────────
// The addressable sub-editor routes (item 6 / §2 of the routing doc):
//   /campaigns/audiences/:boardId/edit   → audience-board contacts editor
//   /campaigns/templates/:templateId/edit → template editor
// These give the board/template editors a URL of their own so reload +
// back/forward work (instead of relying on the legacy in-tab activeBulkView
// swap alone, which is lost on reload). We REUSE the real editors via
// `CampaignManagementView` (which renders BulkTaskViewContainer / NewTemplateView
// for the board / template sub-views) — only the ENTRY changes: this adapter
// drives the legacy Redux to the right sub-view from the URL entity id on mount
// (mirroring the canonical `useRestoreCampaignViewFromUrl` deep-link logic) and
// resets it on unmount, exactly like the other adapters in this file.
// 'template-new' is the CREATE variant of the template editor (no entity id):
// it drives the same `campaignNewTemplateViewFromManagement` view so
// `NewTemplateView` mounts, but resets the editor WITHOUT seeding a template,
// so the user gets a blank new-template form. Audience CREATE has no routable
// surface (it's a modal-driven import flow — see nav-context / the report), so
// there is intentionally no 'audience-new' kind here.
type SubEditorKind = 'audience' | 'template' | 'template-new';

interface CampaignSubEditorProps {
  kind: SubEditorKind;
  /** The board id (audience) or template id (template) from the route. Absent
   *  for the create variants ('template-new'). */
  entityId?: string;
  onBack: () => void;
}

const SUB_EDITOR_BULK_VIEW: Record<SubEditorKind, BulkView> = {
  audience: 'campaignBoardFromManagementView',
  template: 'campaignNewTemplateViewFromManagement',
  'template-new': 'campaignNewTemplateViewFromManagement',
};
const SUB_EDITOR_TAB: Record<SubEditorKind, BulkDashboardTab> = {
  audience: 'manageAudiences',
  template: 'manageTemplates',
  'template-new': 'manageTemplates',
};

export const CampaignSubEditor: React.FC<CampaignSubEditorProps> = ({ kind, entityId }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(namespace);
  // useStore (not useSelector): we only need a one-shot lookup after a fetch
  // resolves, with no re-render dependency (mirrors useRestoreCampaignViewFromUrl).
  const store = useStore<CommonRootState>();
  const prevBulkView = useAppSelector((s: CommonRootState) => s.CampaignReducer.activeBulkView);
  const prevTab = useAppSelector((s: CommonRootState) => s.CampaignReducer.activeTab);
  const restoreRef = React.useRef<{ bulkView: BulkView; tab: BulkDashboardTab }>({
    bulkView: prevBulkView,
    tab: prevTab,
  });

  React.useEffect(() => {
    const restore = restoreRef.current;
    dispatch(setActiveTab(SUB_EDITOR_TAB[kind]));
    dispatch(setActiveBulkView(SUB_EDITOR_BULK_VIEW[kind]));

    if (kind === 'audience' && entityId) {
      dispatch(bulkAudienceSelectBoardThunk({ boardId: entityId }));
      // Cold deep-link: bootstrap the board into the store if absent.
      dispatch(DBBoardsThunks.find({ _id: entityId }));
    } else if (kind === 'template-new') {
      // CREATE: reset the editor, then seed the SAME blank template the legacy
      // "New Template" button seeds (bulk-template-dialog.handleNewTemplateButtonClick)
      // so NewTemplateView (rendered for `campaignNewTemplateViewFromManagement`)
      // shows an identical blank new-template form with the default name.
      dispatch(resetScheduledMessageEditor());
      dispatch(setCurrentTemplate({
        name: t('templates.newTemplateDefaultName'),
        message: '',
        asset: undefined,
        imageId: '',
        sendLinkPreview: false,
        linkPreview: {},
        isPoll: false,
        poll: {},
      }));
    } else if (kind === 'template' && entityId) {
      // EDIT: reset the editor first so no prior draft bleeds into this one,
      // then seed currentTemplate (cached, or fetched on a cold deep-link).
      dispatch(resetScheduledMessageEditor());
      const cached = templateSelectOneObjectByQuery(store.getState().DBTemplateReducer, { _id: entityId });
      if (cached) {
        dispatch(setCurrentTemplate(cached));
        dispatch(loadTemplateData(cached));
      } else {
        dispatch(DBTemplateThunks.find({ _id: entityId })).then(() => {
          const fresh = templateSelectOneObjectByQuery(store.getState().DBTemplateReducer, { _id: entityId });
          if (fresh) {
            dispatch(setCurrentTemplate(fresh));
            dispatch(loadTemplateData(fresh));
          }
        });
      }
    }

    return () => {
      dispatch(setActiveBulkView(restore.bulkView));
      dispatch(setActiveTab(restore.tab));
      dispatch(resetCampaignAndTemplateThunk());
      // Audience editor: reset the selected board on exit, exactly as the legacy
      // back path does (handleActiveBulkViewNavigationThunk 'fromCampaignBoardView').
      // The redesign's own back button (redesignNav.closeBoard) returns before
      // that legacy thunk runs, so without this the selection lingers. On a second
      // open currentBoard._id would be UNCHANGED, so tasks-panel's
      // [currentBoard?._id] load effect never re-fires — and it races behind
      // bulkAudienceSelectBoardThunk's setBulkTasksMap({}) clear — leaving the
      // board empty. Resetting here makes every re-open a clean '' → boardId
      // transition, identical to the first open.
      if (kind === 'audience') {
        dispatch(boardsActions.privateSetSelectedBoardId(''));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, kind, entityId]);

  return (
    <div className="scroll" style={{ flex: 1, padding: '8px 0 24px' }}>
      <CampaignManagementView hideTabs />
    </div>
  );
};

// ─── CampaignLogsRoute ───────────────────────────────────────────────────────
// The addressable message-logs route: /campaigns/:campaignId/logs. The legacy
// logs view (`BulkLogsView`) is Redux-driven — it reads `campaignIdForLogsView`
// and renders when `activeBulkView === 'campaignLogsView'`. We REUSE it as-is and
// only change the ENTRY: drive that Redux from the URL campaign id on mount
// (mirroring CampaignSubEditor + the legacy card/row-menu "View logs" handlers),
// and restore the prior flags on unmount so leaving never leaks the logs view
// into another surface. This makes logs reload-/back-safe instead of a transient
// in-place activeBulkView swap.
interface CampaignLogsRouteProps {
  campaignId: string;
  onBack: () => void;
}

export const CampaignLogsRoute: React.FC<CampaignLogsRouteProps> = ({ campaignId, onBack }) => {
  const dispatch = useAppDispatch();
  const prevBulkView = useAppSelector((s: CommonRootState) => s.CampaignReducer.activeBulkView);
  const prevTab = useAppSelector((s: CommonRootState) => s.CampaignReducer.activeTab);
  const restoreRef = React.useRef<{ bulkView: BulkView; tab: BulkDashboardTab }>({
    bulkView: prevBulkView,
    tab: prevTab,
  });

  React.useEffect(() => {
    const restore = restoreRef.current;
    dispatch(setCampaignIdForLogsView(campaignId));
    dispatch(setActiveTab('manageCampaigns'));
    dispatch(setActiveBulkView('campaignLogsView'));
    // Cold deep-link / reload: BulkLogsView returns null until the campaign is in
    // the store (it keys on campaignsSelectOneObjectByQuery), so bootstrap it —
    // mirrors CampaignSubEditor's audience-board find. (In-app navigation already
    // has it loaded; this only matters for a fresh URL hit.)
    dispatch(DBCampaignThunks.find({ _id: campaignId }));
    return () => {
      dispatch(setActiveBulkView(restore.bulkView));
      dispatch(setActiveTab(restore.tab));
      dispatch(setCampaignIdForLogsView(''));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, campaignId]);

  // BulkLogsView owns its own full-height layout (header + internal scroll),
  // mirroring how the detail surface renders <SequenceDetail/> directly — so we
  // render it bare rather than wrapping it in a second scroll container.
  return <BulkLogsView onBack={onBack} />;
};
